import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { seoAnalytics } from '@/lib/api'

interface CrawlErrorsTableProps {
  days: number
  section?: string
}

export default function CrawlErrorsTable({ days, section }: CrawlErrorsTableProps) {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['seo-crawl-errors', days, section],
    queryFn: () => seoAnalytics.getCrawlErrors(days, 50, section),
  })

  const errors = data?.data || []

  const copyToClipboard = async (url: string) => {
    await navigator.clipboard.writeText(url)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  const getStatusColor = (status: number) => {
    if (status >= 500) return 'text-danger-600 bg-danger-100 dark:bg-danger-900/30'
    if (status >= 400) return 'text-warning-600 bg-warning-100 dark:bg-warning-900/30'
    return 'text-slate-600 bg-slate-100 dark:bg-slate-700'
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-warning-100 dark:bg-warning-900/30">
            <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Errores de Rastreo</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              URLs con errores 404/500 detectados por bots
            </p>
          </div>
        </div>
        {errors.length > 0 && (
          <span className="badge badge-warning">{errors.length} errores</span>
        )}
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-6">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="skeleton h-6 w-12 rounded" />
                  <div className="skeleton h-4 flex-1" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : errors.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No se han detectado errores de rastreo</p>
            <p className="text-sm mt-1">Esto es bueno para el SEO</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Bot
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Hits
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ultimo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {errors.map((error: any, idx: number) => (
                  <tr
                    key={idx}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium',
                          getStatusColor(error.status_code)
                        )}
                      >
                        {error.status_code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 max-w-xs">
                        <span
                          className="text-sm text-slate-700 dark:text-slate-300 truncate"
                          title={error.url_path}
                        >
                          {error.url_path}
                        </span>
                        <button
                          onClick={() => copyToClipboard(error.url_path)}
                          className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                          title="Copiar URL"
                        >
                          {copiedUrl === error.url_path ? (
                            <Check className="w-3.5 h-3.5 text-success-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-slate-400" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {error.bot_name || 'Desconocido'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {error.hit_count?.toLocaleString() || 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {error.last_seen
                          ? new Date(error.last_seen).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: 'short',
                            })
                          : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
